/**
 * Auth context provider — BetterAuth-backed.
 *
 * Same exposed shape as the previous Supabase implementation so all
 * existing consumers (route guards, useAuth() callers) keep working
 * unchanged. Internal data flow:
 *   - useSession() from better-auth/react gives session + loading state
 *   - Profile (role, onboardingCompleted) is fetched from
 *     /me/profile via the API client when the session resolves
 *   - Session changes trigger a profile refetch
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getGreetingName } from '@formency/shared';
import { authClient, useSession, signOut as authSignOut } from '@/shared/auth/authClient';
import { get, setSigningOut } from '@/shared/api/client';

interface Role {
  slug: string;
  name?: string;
}

// BetterAuth session shape (subset). Full type is exported by better-auth/react
// but we only consume id/email/name/createdAt here.
interface BetterAuthUser {
  id: string;
  email: string;
  name?: string | null;
  emailVerified?: boolean;
  createdAt?: string | Date;
  // Surfaced by the BetterAuth two-factor plugin's user-schema extension.
  // Optional in the type because the field only exists once the plugin's
  // migration has run; treat undefined as "not enabled".
  twoFactorEnabled?: boolean | null;
}

interface BetterAuthSession {
  user: BetterAuthUser;
  session: {
    id: string;
    expiresAt: Date | string;
  };
}

interface AuthContextType {
  loading: boolean;
  session: BetterAuthSession | null;
  user: BetterAuthUser | null;
  firstName: string | null;
  role: Role | null;
  onboardingCompleted: boolean;
  setOnboardingCompleted: (value: boolean) => void;
  /** True when the signed-in user has TOTP 2FA enabled. */
  twoFactorEnabled: boolean;
  isAuthed: boolean;
  isAdmin: boolean;
  isUserManager: boolean;
  isContentManager: boolean;
  isInstructor: boolean;
  isSupport: boolean;
  isLearner: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

/** Role hierarchy levels — higher levels include all permissions of lower levels. */
const ROLE_LEVELS: Record<string, number> = {
  admin: 100,
  user_manager: 80,
  content_manager: 60,
  instructor: 40,
  support: 20,
  learner: 10,
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  // useSession() reactively returns BetterAuth's session state.
  // `data` is { user, session } | null; `isPending` is the loading flag.
  const { data: session, isPending: sessionLoading } = useSession();

  const [role, setRole] = useState<Role | null>(null);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  // Tracks the userId we've ACTUALLY fetched profile data for. Compared
  // synchronously during render to the current session userId — if they
  // don't match, `loading` stays true, blocking RequireLearnerOnboarding
  // from reading stale `onboardingCompleted` and bouncing users to
  // /welcome. Updates inside fetchProfile's finally block.
  //
  // Why a state (not a ref): refs don't trigger re-renders. We need
  // `loading` to flip from true→false when the value changes, which only
  // works with state.
  //
  // Race this guards against: post-sign-in, useSession's data updates
  // to the new userId BEFORE the useEffect fires fetchProfile. Render
  // happens with new userId but stale onboardingCompleted=false (last
  // set in the sign-out branch). Without this comparison, loading=false
  // and the route guard reads the stale flag.
  const [profileFetchedForUserId, setProfileFetchedForUserId] = useState<string | null>(null);
  const lastUserIdRef = useRef<string | null>(null);
  // Distinguishes "session just loaded for the first time" from "user
  // signed in mid-session". Without this guard the welcome-back toast
  // would fire on every page refresh because the initial useSession
  // resolves from `undefined` to a real session within a render or two,
  // which our diff-based useEffect treats as a sign-in event.
  const hasFinishedInitialLoadRef = useRef(false);

  const fetchProfile = useCallback(
    async (forUserId: string): Promise<void> => {
      setProfileLoading(true);
      try {
        const profile = await queryClient.fetchQuery({
          queryKey: ['profile', 'me'],
          queryFn: () => get<Record<string, unknown>>('/me/profile'),
        });

        if (!profile) {
          console.error('[profile fetch error] No profile found');
          setRole(null);
          setOnboardingCompleted(false);
          return;
        }

        const p = profile as Record<string, unknown>;
        setOnboardingCompleted(!!p.learnerOnboardingCompleted);

        let roleData: Role | null = null;
        const rawRole = p.role ?? p.roles;
        if (rawRole) {
          if (typeof rawRole === 'string') {
            roleData = { slug: rawRole };
          } else if (typeof rawRole === 'object' && rawRole !== null && 'slug' in rawRole) {
            roleData = rawRole as Role;
          } else {
            console.error('[profile fetch error] unexpected role format:', rawRole);
          }
        } else {
          console.error('[profile fetch error] No role found');
        }

        setRole(roleData);
        // Note: we no longer pull firstName/name from /me/profile. The user's
        // name is canonical on BetterAuth `user.name` and consumed via the
        // session — see the `firstName` derivation in the useMemo below.
      } catch (e) {
        console.error('[profile fetch threw]', e);
        setRole(null);
      } finally {
        setProfileLoading(false);
        // Mark profile as fetched for THIS specific user id — even if the
        // fetch errored. The error path leaves role/onboardingCompleted at
        // their defaults; the user can still navigate (consumers will read
        // the defaults, RequireLearnerOnboarding will still redirect to
        // /welcome but at least the loading flag isn't stuck on).
        setProfileFetchedForUserId(forUserId);
      }
    },
    [queryClient]
  );

  // Sync with BetterAuth session changes.
  useEffect(() => {
    // Wait for useSession() to settle before treating a userId as a real
    // signal. While `sessionLoading` is true the value of `session` is
    // unstable; reacting to it produces false sign-in events on every
    // page load.
    if (sessionLoading) return;

    const userId = session?.user?.id ?? null;
    const wasInitialLoad = !hasFinishedInitialLoadRef.current;
    hasFinishedInitialLoadRef.current = true;

    if (userId !== lastUserIdRef.current) {
      lastUserIdRef.current = userId;
      if (userId) {
        // Whether this is initial-hydration-with-session or a real
        // mid-tab sign-in, we still need the profile fetched.
        fetchProfile(userId);
        // Toast on a genuine sign-in event only — wasInitialLoad means
        // this is the first run after sessionLoading flipped to false,
        // i.e. the user was ALREADY signed in (page refresh) and we
        // shouldn't fire a "welcome back" for that.
        if (!wasInitialLoad) {
          // Greeting name comes from the BetterAuth session directly
          // (user.name) rather than waiting on /me/profile — name is
          // already populated on the session by this point.
          const greetingName = session?.user
            ? getGreetingName({
                name: session.user.name ?? null,
                email: session.user.email ?? null,
              })
            : null;
          if (greetingName && greetingName !== 'there') {
            toast.success(`Welcome back, ${greetingName}!`);
          } else {
            toast.success('Sign-in successful!');
          }
        }
      } else {
        // Sign-out / session-cleared path. Don't toast here —
        // sessionExpired.ts owns the "session expired" UX and signOut()
        // owns the explicit-sign-out toast.
        queryClient.clear();
        setRole(null);
        setOnboardingCompleted(false);
        // Reset so the next sign-in (with any userId, even the same
        // one) triggers a fresh fetch. The mismatch comparison in the
        // loading flag does the rest.
        setProfileFetchedForUserId(null);
      }
    }
    // We intentionally only watch session?.user?.id — name/email changes
    // mid-session (e.g. user updates their email) shouldn't re-fire the
    // welcome-back toast; the toast is bound to userId transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionLoading, session?.user?.id, fetchProfile, queryClient]);

  // The BetterAuth two-factor plugin adds twoFactorEnabled to the user
  // schema. The strict client type from useSession() doesn't always surface
  // plugin-added fields, so we read it through a narrow cast. Hoisted out
  // of useMemo so the value can be a simple identifier in the dependency
  // array (the react-hooks lint rules reject inline casts in deps).
  const twoFactorEnabled = !!(session?.user as { twoFactorEnabled?: boolean | null } | undefined)
    ?.twoFactorEnabled;

  const value = useMemo(() => {
    const user = session?.user ?? null;
    const roleLevel = role?.slug ? (ROLE_LEVELS[role.slug] ?? 0) : 0;
    // Derive `firstName` from BetterAuth's `user.name` for backwards-compat
    // with consumers that read it. `user.name` is now the single canonical
    // name source; we expose the first whitespace-delimited word here so
    // greetings ("Welcome back, X!") and AI chat prompts get a sensible
    // short-form name. Returns null when the user is signed out, mirroring
    // the previous shape.
    const firstName = user
      ? getGreetingName({ name: user.name ?? null, email: user.email ?? null })
      : null;

    // SYNCHRONOUS check during render: if the session userId doesn't match
    // the userId we last fetched profile for, we're in a stale-data window
    // and consumers must wait. This is what keeps RequireLearnerOnboarding
    // from reading stale `onboardingCompleted` and bouncing users to
    // /welcome after sign-in. Compare in render (not in an effect) so the
    // FIRST render after a userId change already has loading=true.
    const profileStale = !!user && profileFetchedForUserId !== user.id;

    return {
      loading: sessionLoading || profileLoading || profileStale,
      session: (session as BetterAuthSession | null) ?? null,
      user,
      firstName,
      role,
      onboardingCompleted,
      setOnboardingCompleted,
      twoFactorEnabled,
      isAuthed: !!user,
      isAdmin: roleLevel >= ROLE_LEVELS.admin,
      isUserManager: roleLevel >= ROLE_LEVELS.user_manager,
      isContentManager: roleLevel >= ROLE_LEVELS.content_manager,
      isInstructor: roleLevel >= ROLE_LEVELS.instructor,
      isSupport: roleLevel >= ROLE_LEVELS.support,
      isLearner: roleLevel >= ROLE_LEVELS.learner,
      async signOut() {
        setSigningOut(true);
        try {
          await authSignOut();
          // Synchronous cleanup — don't rely on the userId-transition
          // useEffect alone. If useSession() returns a stable reference
          // even after sign-out (debouncing, polling skew), the effect
          // wouldn't fire and _isSigningOut would stay true forever,
          // breaking the next sign-out attempt.
          queryClient.clear();
          setRole(null);
          setOnboardingCompleted(false);
          setSigningOut(false);
          toast.success('Signed out successfully!');
        } catch (error: unknown) {
          setSigningOut(false);
          throw error;
        }
      },
    };
    // Narrow deps to specific session fields read below — NOT the whole
    // `session` object, which BetterAuth may return as a fresh reference
    // on every poll/refresh, causing every useAuth() consumer to re-render
    // unnecessarily. Downstream consumers only check `isAuthed`/`user`/
    // `role`-level fields; they don't depend on session reference identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sessionLoading,
    profileLoading,
    profileFetchedForUserId,
    session?.user?.id,
    session?.user?.email,
    session?.user?.name,
    // Watch this so enabling/disabling 2FA mid-session re-renders consumers
    // (settings row badge, modal initial view) without a full reload.
    twoFactorEnabled,
    session?.session?.id,
    role,
    onboardingCompleted,
    queryClient,
  ]);

  // Touch authClient so unused-import lint doesn't trip — also surfaces
  // a clear runtime hint if the singleton failed to initialise.
  void authClient;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
